'use client'

import { useState, useRef } from 'react'

import dynamic from 'next/dynamic'
const LightboxComponent = dynamic(() => import('@/components/lightbox/Lightbox'))

import AppImage from '../AppImage'

import { ImageFieldProps } from '@/types/types'

type GalleryImage = ImageFieldProps

type GalleryProps = {
  images: GalleryImage[]
  lazy?: boolean
}

export default function Gallery({ images, lazy = true }: GalleryProps) {
  const [index, setIndex] = useState(-1)

  // Store references to preview elements so we can dynamically query
  const previewRefs = useRef([])

  if (!images || !images.length) {
    return null
  }

  const thumbnailTransforms = {
    dpr: '2',
    width: '402',
    height: '226',
  }

  const pictureSources = {
    // 4 column desktop
    '(min-width: 989px)': { ...thumbnailTransforms },

    // 3 column tablet
    '(min-width: 769px)': { width: 297, height: 200, dpr: 2 },

    // 2 column mobile
    '(min-width: 399px)': { width: 330, height: 210, dpr: 2 },

    // 2 column small mobile
    '(max-width: 399px)': { width: 174, height: 174, dpr: 2 },
  }

  const limit = 8
  const previewImages = images.slice(0, limit)

  const singleTemplateClass = `
    @[0px]/single-simple:!grid-cols-2
    @[0px]/nested-flex:!grid-cols-2
    @[600px]/single-simple:!grid-cols-3
    @[600px]/nested-flex:!grid-cols-3
  `

  return (
    <div className="component component--gallery">
      <div className="container @[0px]/nested-flex:!max-w-[var(--container-s-max-w)] @[0px]/single-simple:!max-w-[var(--container-s-max-w)]">
        {/* Gallery Thumbnails */}
        <div className="flex flex-col gap-m">
          <div
            className={`grid w-full ms:grid-cols-2 ms:gap-xs ms-up:grid-cols-3 ms-up:gap-m m-up:grid-cols-4 ${singleTemplateClass}`}>
            {previewImages.map((image, index) => {
              return (
                <button
                  ref={(el) => {
                    previewRefs.current[index] = el
                  }}
                  key={index}
                  onClick={() => setIndex(index)}
                  className={`relative ${index >= 4 ? 'ms:hidden' : ''} ${index >= 6 ? '@[0px]/nested-flex:hidden @[0px]/single-simple:hidden m:hidden' : ''}`}>
                  <AppImage
                    path={image.url}
                    alt={image.alt}
                    height={thumbnailTransforms.height}
                    width={thumbnailTransforms.width}
                    transforms={thumbnailTransforms}
                    pictureSources={pictureSources}
                    loading={lazy ? 'lazy' : 'eager'}
                    className="h-full w-full object-cover"
                  />
                </button>
              )
            })}
          </div>

          {images.length > limit && (
            <div className="p--small flex items-center justify-between gap-l">
              <div>
                <span>1-</span>
                <span className="ms-up:hidden">{Math.min(4, images.length)}</span>
                <span className="ms:hidden m-up:hidden">{Math.min(6, images.length)}</span>
                <span className="m:hidden">{Math.min(limit, images.length)}</span>
                <span> of {images.length}</span>
              </div>
              <button
                className="justify-self-end font-bold"
                onClick={() => {
                  // detect how many preview elements are visible
                  const visible = previewRefs.current.filter(
                    (ref) => window.getComputedStyle(ref).display !== 'none',
                  ).length

                  const startIndex = visible ? visible : 1

                  setIndex(startIndex)
                }}>
                View all &gt;
              </button>
            </div>
          )}
        </div>

        {/* Lightbox */}
        {index >= 0 ? (
          <LightboxComponent
            index={index}
            slides={images.map((image) => ({
              src: image.url,
              alt: image.alt,
              height: image.height,
              width: image.width,
            }))}
            open={index >= 0}
            close={() => setIndex(-1)}
            animation={{
              fade: 400,
              swipe: 400,
              navigation: 0,
            }}
            plugins={['zoom', 'counter']}
          />
        ) : null}
      </div>
    </div>
  )
}
